/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { MonthlyActivity } from '../../types';
import { apiClient } from '../../services/api';
import './ActivityReport.css';

const ActivityReport: React.FC = () => {
  const navigate = useNavigate();
  const [monthlyActivities, setMonthlyActivities] = useState<MonthlyActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getMonthlyActivityReport();

      if (response.success && response.report) {
        setMonthlyActivities(response.report.monthlyActivities);
      } else {
        setError(response.error || 'Failed to load report');
      }
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.message || 'An error occurred while loading the report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="activity-report-container">
      <BackButton to="/" />
        <div className="loading">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-report-container">
        <div className="error-message">
          <h3>Error Loading Report</h3>
          <p>{error}</p>
          <button onClick={fetchReport} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalBids = monthlyActivities.reduce((sum, month) => sum + month.bidCount, 0);
  const totalComponents = monthlyActivities.reduce((sum, month) => sum + month.componentCount, 0);

  return (
    <div className="activity-report-container">
      <div className="report-header">
        <h2>Monthly Activity Report</h2>
        <div className="report-summary">
          <div className="summary-card">
            <div className="summary-label">Total Bids</div>
            <div className="summary-value">{totalBids}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total PDFs</div>
            <div className="summary-value">{totalComponents}</div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Bids Created</th>
              <th>PDFs Uploaded</th>
            </tr>
          </thead>
          <tbody>
            {monthlyActivities.map((activity) => (
              <tr
                key={activity.month}
                onClick={() => navigate(`/reports/activity/${activity.month}`)}
                style={{ cursor: 'pointer' }}
                className="clickable-row"
              >
                <td className="month-name">{activity.monthName}</td>
                <td className="bid-count">{activity.bidCount}</td>
                <td className="pdf-count">{activity.componentCount}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="total-label">Total</td>
              <td className="total-value">{totalBids}</td>
              <td className="total-value">{totalComponents}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ActivityReport;
